import type { CapiPlp } from 'types/capi/plp';
import type { ProductListItemInternal } from 'types/capi/shared';

import FlameIcon from '~/assets/icons/Flame';
import Image from '~/components/Image';
import NotificationTooltip from '~/components/NotificationTooltip';
import ProductCard from '~/components/ProductCard';
import usePageData from '~/hooks/usePageData';

function getProductChunks(
  [...products]: ProductListItemInternal[],
  chunkSize: number
) {
  const chunks = [];

  while (products.length) chunks.push(products.splice(0, chunkSize));

  return chunks;
}

type CarouselProps = {
  numberVisible: number;
  products: ProductListItemInternal[];
};

const Carousel = ({ products, numberVisible }: CarouselProps) => {
  return (
    <div className="flex w-full gap-2 overflow-x-scroll px-4 md:gap-6 md:px-8">
      {getProductChunks(products, numberVisible).map((chunk, chunkIndex) => (
        <div
          key={`products-chunk-${chunkIndex}`}
          style={{
            width: `${(chunk.length / numberVisible) * 100}%`
          }}
          className={`grid-cols-${chunk.length} grid shrink-0 gap-2 md:gap-6 ${
            chunk.length === 1 ? 'px-[2px] md:px-2' : ''
          }`}
        >
          {chunk.map((product, productIndex) => (
            <ProductCard.Internal
              key={`product-${productIndex}`}
              product={product}
              padImage
              className="w-full"
              allowNotifications
              imageLoading="eager"
              cardSize="small"
              showNotificationCount={true}
              pagePosition="PLP Popular Carousel"
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const PopularReleasesCarousel = () => {
  const { popular_products } = usePageData<CapiPlp>();

  if (!popular_products) return null;

  return (
    <div className="relative mb-4 w-full md:py-10">
      <Image
        alt="sole-market-hub-sign-up-banner"
        src="https://cms-cdn.thesolesupplier.co.uk/2024/04/whats-hot-banner.jpg"
        className="absolute left-0 top-0 size-full object-cover brightness-75"
        loading="lazy"
        width={672}
        quality={100}
        responsiveOptions={{
          lg: { width: 1024 },
          md: { width: 768 },
          sm: { width: 672 },
          xl: { width: 1440 }
        }}
      />
      <div className="relative mx-auto flex h-fit max-w-6xl flex-col items-center justify-start gap-6 py-10 text-white md:py-4">
        <div className="flex w-full flex-col gap-2 px-4 md:px-8">
          <span className="flex w-full items-center justify-start gap-2">
            <FlameIcon />
            <span className="text-lg font-bold">Most popular releases</span>
          </span>
          <div className="flex flex-col justify-between gap-4 md:flex-row">
            <span className="text-sm font-normal leading-6">
              The pairs with most notifications set in the last 7 days. Tap the
              bell to get notified.
            </span>
            <NotificationTooltip className="min-w-[255px]" />
          </div>
        </div>
        <div className="hidden md:block">
          <Carousel products={popular_products} numberVisible={4} />
        </div>
        <div className="md:hidden">
          <Carousel products={popular_products} numberVisible={2} />
        </div>
      </div>
    </div>
  );
};

export default PopularReleasesCarousel;
